import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { Skeleton } from './Skeleton';

export interface SecureImageProps {
  /** API path that streams the image, e.g. `/platform/institutes/:uuid/logo`. */
  path: string;
  alt: string;
  className?: string;
  /** Rendered when there is no image, or it failed to load. */
  fallback?: React.ReactNode;
  /** Change this to force a reload after an upload. */
  reloadKey?: string | number;
}

/**
 * Displays an image that requires authentication.
 *
 * Uploaded files are private (CLAUDE.md §11) — there is no public URL to put in
 * `<img src>`. So the bytes are fetched with the access token like any other
 * request and turned into an object URL, which is revoked on unmount so the
 * blob does not leak.
 */
export function SecureImage({ path, alt, className, fallback = null, reloadKey }: SecureImageProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    let isActive = true;
    let createdUrl: string | null = null;

    setStatus('loading');

    apiClient
      .get<Blob>(path, { responseType: 'blob' })
      .then((response) => {
        if (!isActive) return;
        createdUrl = URL.createObjectURL(response.data);
        setObjectUrl(createdUrl);
        setStatus('ready');
      })
      .catch(() => {
        // A missing logo is the normal case, not an error worth surfacing.
        if (isActive) setStatus('failed');
      });

    return () => {
      isActive = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [path, reloadKey]);

  if (status === 'loading') return <Skeleton className={cn('h-full w-full', className)} />;
  if (status === 'failed' || !objectUrl) return <>{fallback}</>;

  return <img src={objectUrl} alt={alt} className={cn('object-contain', className)} />;
}
